import { useEffect, useMemo, useState } from 'react';
import {
  ORDRE_TYPES,
  STYLES_JOUR,
  compterParType,
  numeroSemaineIso,
  type JourCalendrierLocal,
  type TypeJour,
} from '../../lib/calendrier-utils';

/** Identité portée par l'en-tête du modèle Excel remis par le client. */
export interface EnteteCalendrier {
  beneficiaire?: string | null;
  formation?: string | null;
  periode?: string | null;
  horaires?: string | null;
}

interface Props {
  jours: JourCalendrierLocal[];
  /** Année de septembre : le calendrier court de septembre N à août N+1. */
  anneeDebut: number;
  entete?: EnteteCalendrier;
  /**
   * Un samedi ou un dimanche affecté « entreprise » ou « centre » par le remplissage
   * par défaut n'est pas un jour travaillé : il est rendu neutre. Les affectations
   * réelles d'un week-end (férié, vacances, épreuve, ECF) restent visibles.
   */
  neutraliserWeekEnd?: boolean;
  /**
   * Rend la grille modifiable : un appui peint le jour, un glissé peint ceux
   * qu'il traverse. Seuls les jours présents dans `jours` se peignent — ceux
   * hors de la période programmée restent neutres.
   */
  onPeindre?: (date: Date) => void;
}

const MOIS_SCOLAIRES = [
  { index: 8, nom: 'Septembre', decalageAnnee: 0 },
  { index: 9, nom: 'Octobre', decalageAnnee: 0 },
  { index: 10, nom: 'Novembre', decalageAnnee: 0 },
  { index: 11, nom: 'Décembre', decalageAnnee: 0 },
  { index: 0, nom: 'Janvier', decalageAnnee: 1 },
  { index: 1, nom: 'Février', decalageAnnee: 1 },
  { index: 2, nom: 'Mars', decalageAnnee: 1 },
  { index: 3, nom: 'Avril', decalageAnnee: 1 },
  { index: 4, nom: 'Mai', decalageAnnee: 1 },
  { index: 5, nom: 'Juin', decalageAnnee: 1 },
  { index: 6, nom: 'Juillet', decalageAnnee: 1 },
  { index: 7, nom: 'Août', decalageAnnee: 1 },
];

const ENTETES_SEMAINE = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const TYPES_HORS_WEEK_END: TypeJour[] = ['CENTRE_FORMATION', 'ENTREPRISE'];

function cleJour(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Vue annuelle en douze mois, reprise de la structure du modèle Excel validé
 * (mois en blocs, semaines en lignes, colonne « Sem. »). Elle remplace la grille
 * de 12 colonnes × 31 lignes pour la consultation : celle-ci ne tient pas sur une
 * tablette sans défilement horizontal, alors que douze blocs se réorganisent.
 */
export default function CalendrierAnnuel({
  jours,
  anneeDebut,
  entete,
  neutraliserWeekEnd = true,
  onPeindre,
}: Props): JSX.Element {
  const [peinture, setPeinture] = useState(false);
  useEffect(() => {
    if (!peinture) return;
    const arreter = () => setPeinture(false);
    window.addEventListener('pointerup', arreter);
    return () => window.removeEventListener('pointerup', arreter);
  }, [peinture]);
  const edition = onPeindre
    ? {
        commencer: (date: Date) => {
          setPeinture(true);
          onPeindre(date);
        },
        survoler: (date: Date) => {
          if (peinture) onPeindre(date);
        },
      }
    : null;
  const parDate = useMemo(() => {
    const table = new Map<string, TypeJour>();
    jours.forEach((jour) => table.set(cleJour(jour.date), jour.type));
    return table;
  }, [jours]);

  const compte = useMemo(() => compterParType(jours), [jours]);
  const champsEntete = [
    ['Bénéficiaire', entete?.beneficiaire],
    ['Formation', entete?.formation],
    ['Période', entete?.periode],
    ['Horaires', entete?.horaires],
  ].filter(([, valeur]) => Boolean(valeur)) as [string, string][];

  return (
    <div className="space-y-5">
      {champsEntete.length > 0 && (
        <dl className="grid grid-cols-1 gap-3 rounded-2xl bg-codialis-sand p-4 sm:grid-cols-2 lg:grid-cols-4">
          {champsEntete.map(([libelle, valeur]) => (
            <div key={libelle} className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                {libelle}
              </dt>
              <dd className="mt-0.5 truncate text-sm font-semibold text-codialis-navy" title={valeur}>
                {valeur}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* La légende annonce un volume : une couleur seule ne dit pas combien de jours. */}
      <ul className="flex flex-wrap items-center gap-2">
        {ORDRE_TYPES.filter((type) => compte[type] > 0).map((type) => (
          <li
            key={type}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${STYLES_JOUR[type].pastille}`}
          >
            {STYLES_JOUR[type].libelle}
            <span className="rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
              {compte[type]} j
            </span>
          </li>
        ))}
      </ul>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {MOIS_SCOLAIRES.map((mois) => (
          <MiniMois
            key={mois.nom}
            nom={mois.nom}
            annee={anneeDebut + mois.decalageAnnee}
            indexMois={mois.index}
            parDate={parDate}
            neutraliserWeekEnd={neutraliserWeekEnd}
            edition={edition}
          />
        ))}
      </div>
    </div>
  );
}

function MiniMois({
  nom,
  annee,
  indexMois,
  parDate,
  neutraliserWeekEnd,
  edition,
}: {
  nom: string;
  annee: number;
  indexMois: number;
  parDate: Map<string, TypeJour>;
  neutraliserWeekEnd: boolean;
  edition: { commencer: (date: Date) => void; survoler: (date: Date) => void } | null;
}): JSX.Element {
  const semaines = useMemo(() => {
    const premier = new Date(annee, indexMois, 1);
    const nbJours = new Date(annee, indexMois + 1, 0).getDate();
    // Lundi = 0 : le modèle client commence la semaine au lundi, pas au dimanche.
    const decalage = (premier.getDay() + 6) % 7;

    const cases: (Date | null)[] = Array.from({ length: decalage }, () => null);
    for (let jour = 1; jour <= nbJours; jour += 1) {
      cases.push(new Date(annee, indexMois, jour));
    }
    while (cases.length % 7 !== 0) cases.push(null);

    const lignes: (Date | null)[][] = [];
    for (let debut = 0; debut < cases.length; debut += 7) {
      lignes.push(cases.slice(debut, debut + 7));
    }
    return lignes;
  }, [annee, indexMois]);

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="font-display text-sm font-bold text-codialis-navy">{nom}</h3>
        <span className="text-[11px] font-medium text-codialis-gray">{annee}</span>
      </header>

      <table className="w-full table-fixed border-separate border-spacing-[2px]">
        <thead>
          <tr>
            <th className="w-7 pb-1 text-[10px] font-semibold uppercase text-codialis-gray">Sem.</th>
            {ENTETES_SEMAINE.map((lettre, position) => (
              <th
                key={`${lettre}-${position}`}
                scope="col"
                className={`pb-1 text-[10px] font-semibold uppercase ${
                  position >= 5 ? 'text-gray-300' : 'text-codialis-gray'
                }`}
              >
                <abbr
                  title={
                    ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'][position]
                  }
                  className="no-underline"
                >
                  {lettre}
                </abbr>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semaines.map((semaine, indexSemaine) => {
            const reference = semaine.find(Boolean);
            return (
              <tr key={indexSemaine}>
                <th
                  scope="row"
                  className="text-[10px] font-medium tabular-nums text-gray-300"
                >
                  {reference ? numeroSemaineIso(reference) : ''}
                </th>
                {semaine.map((date, indexJour) => {
                  if (!date) return <td key={indexJour} aria-hidden="true" />;

                  const weekEnd = indexJour >= 5;
                  const affectation = parDate.get(cleJour(date));
                  const neutralise =
                    !affectation ||
                    (neutraliserWeekEnd && weekEnd && TYPES_HORS_WEEK_END.includes(affectation));
                  const style = neutralise ? null : STYLES_JOUR[affectation];
                  const peignable = edition !== null && affectation !== undefined;

                  return (
                    <td key={indexJour} className="p-0">
                      <div
                        onPointerDown={
                          peignable
                            ? (evenement) => {
                                evenement.preventDefault();
                                edition.commencer(date);
                              }
                            : undefined
                        }
                        onPointerEnter={peignable ? () => edition.survoler(date) : undefined}
                        role={peignable ? 'button' : undefined}
                        title={`${date.toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        })} · ${style ? style.libelle : 'Non affecté'}`}
                        className={`flex h-7 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums select-none ${
                          style ? style.pave : 'bg-gray-50 text-gray-300'
                        } ${peignable ? 'cursor-pointer touch-none [@media(pointer:coarse)]:h-9' : ''}`}
                      >
                        {date.getDate()}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
